import { SecretsManagerClient, GetSecretValueCommand } from '@aws-sdk/client-secrets-manager';
import { SSMClient, GetParameterCommand } from '@aws-sdk/client-ssm';
import { buildProxyClientConfig } from '../utils/aws-proxy.js';
import { getLogger } from '../utils/logger.js';
import { describeAwsFailureForWarn } from './credential-error.js';

/**
 * Resolve `ContainerDefinitions[].Secrets[].ValueFrom` references to real
 * values via the AWS SDK so containers can be started with the secret
 * material injected as plain env vars.
 *
 * Same resolution rules as the ECS Agent: `valueFrom` is either a
 * Secrets Manager secret ARN (optionally with a `:<json-key>::` suffix for
 * key extraction from a JSON blob) or an SSM Parameter ARN. Resolution
 * happens once at startup, before any container boots — partial resolution
 * is meaningless because a "missing" secret would otherwise look like a
 * literal empty string and break the container silently.
 *
 * Failure mode is hard-fail. Mirrors `cdkl invoke --from-state`'s
 * philosophy: explicit failure beats silently-empty. The user fixes their
 * AWS creds / IAM policy / parameter name and re-runs.
 */

export class EcsSecretsResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EcsSecretsResolutionError';
    Object.setPrototypeOf(this, EcsSecretsResolutionError.prototype);
  }
}

export interface SecretEntry {
  /** Container name (only used to thread richer error messages). */
  containerName: string;
  /** Env var name the resolved value lands at. */
  name: string;
  /** Raw ValueFrom (Secrets Manager ARN or SSM Parameter ARN). */
  valueFrom: string;
}

/**
 * Result of secret resolution: each entry maps to the container that
 * requested it plus the resolved value. Returned as a flat list (not
 * grouped) so the caller can populate per-container env maps in one pass.
 */
export interface ResolvedSecret extends SecretEntry {
  value: string;
}

export interface ResolveEcsSecretsOptions {
  /** Region for the AWS SDK clients. Falls back to env defaults when unset. */
  region?: string;
  /**
   * The CLI's `--profile`. Threaded into the Secrets Manager / SSM
   * clients so `secretsmanager:GetSecretValue` / `ssm:GetParameter`
   * authenticate as the profile's account — for `--from-cfn-stack` the
   * secret lives in the deployed (profile) account, and without this the
   * default credential chain authenticates as the wrong account and AWS
   * rejects with AccessDenied. Undefined when `--profile` is unset.
   */
  profile?: string;
  /**
   * Hook for tests: supply pre-built SDK clients. Production callers
   * leave both unset and let the resolver construct + destroy its own
   * clients.
   */
  secretsManagerClient?: SecretsManagerClient;
  ssmClient?: SSMClient;
}

/**
 * Resolve every secret entry in parallel. Returns successfully only when
 * every entry resolved — a single failure aborts the whole batch with the
 * offending container + secret name in the error message.
 */
export async function resolveEcsSecrets(
  entries: SecretEntry[],
  options: ResolveEcsSecretsOptions = {}
): Promise<ResolvedSecret[]> {
  if (entries.length === 0) return [];
  const logger = getLogger().child('ecs-secrets');

  const secretsClient =
    options.secretsManagerClient ??
    new SecretsManagerClient({
      ...buildProxyClientConfig({ profile: options.profile }),
      ...(options.region && { region: options.region }),
      ...(options.profile && { profile: options.profile }),
    });
  const ssmClient =
    options.ssmClient ??
    new SSMClient({
      ...buildProxyClientConfig({ profile: options.profile }),
      ...(options.region && { region: options.region }),
      ...(options.profile && { profile: options.profile }),
    });
  const ownsSecretsClient = options.secretsManagerClient === undefined;
  const ownsSsmClient = options.ssmClient === undefined;

  try {
    const results = await Promise.all(
      entries.map(async (entry) => {
        const value = await resolveOne(entry, secretsClient, ssmClient);
        logger.debug(`Resolved secret ${entry.containerName}.${entry.name} (${entry.valueFrom})`);
        return { ...entry, value };
      })
    );
    return results;
  } finally {
    if (ownsSecretsClient) secretsClient.destroy();
    if (ownsSsmClient) ssmClient.destroy();
  }
}

async function resolveOne(
  entry: SecretEntry,
  secretsClient: SecretsManagerClient,
  ssmClient: SSMClient
): Promise<string> {
  const arn = entry.valueFrom;
  const shape = classifySecretArn(arn);
  switch (shape.kind) {
    case 'secrets-manager':
      return resolveSecretsManager(entry, shape, secretsClient);
    case 'ssm':
      return resolveSsm(entry, shape, ssmClient);
    case 'unknown':
      throw new EcsSecretsResolutionError(
        `Container '${entry.containerName}' secret '${entry.name}' references an unsupported ValueFrom shape '${arn}'. ` +
          'Expected Secrets Manager ARN (optionally with :<json-key>::) or SSM Parameter ARN.'
      );
  }
}

interface SecretsManagerShape {
  kind: 'secrets-manager';
  /** Full ARN minus the optional `:<json-key>::` suffix. */
  baseArn: string;
  /** When set, extract this top-level key from the JSON-decoded SecretString. */
  jsonKey?: string;
}

interface SsmShape {
  kind: 'ssm';
  /** Parameter name (with leading `/`). */
  name: string;
}

interface UnknownShape {
  kind: 'unknown';
}

/**
 * Classify the `ValueFrom` string per the AWS ECS Agent rules. Three
 * accepted shapes:
 *   - `arn:aws:secretsmanager:<region>:<account>:secret:<name>`
 *   - `arn:aws:secretsmanager:<region>:<account>:secret:<name>:<json-key>::`
 *   - `arn:aws:ssm:<region>:<account>:parameter/<name>`
 */
export function classifySecretArn(arn: string): SecretsManagerShape | SsmShape | UnknownShape {
  if (!arn.startsWith('arn:')) return { kind: 'unknown' };

  // Secrets Manager: 7 colon-delimited segments minimum
  // (arn:aws:secretsmanager:region:account:secret:name). The optional
  // json-key suffix appends `:<json-key>::` — exactly two trailing colons
  // because the SecretsManager ARN convention reserves `version-stage`
  // and `version-id` slots that follow.
  const smMatch = /^(arn:[^:]+:secretsmanager:[^:]+:\d+:secret:[^:]+)(?::([^:]+)::?)?$/.exec(arn);
  if (smMatch) {
    const out: SecretsManagerShape = { kind: 'secrets-manager', baseArn: smMatch[1]! };
    if (smMatch[2]) out.jsonKey = smMatch[2];
    return out;
  }

  const ssmMatch = /^arn:[^:]+:ssm:[^:]+:\d+:parameter(\/.+)$/.exec(arn);
  if (ssmMatch) {
    return { kind: 'ssm', name: ssmMatch[1]! };
  }

  return { kind: 'unknown' };
}

async function resolveSecretsManager(
  entry: SecretEntry,
  shape: SecretsManagerShape,
  client: SecretsManagerClient
): Promise<string> {
  let secretString: string | undefined;
  try {
    const resp = await client.send(new GetSecretValueCommand({ SecretId: shape.baseArn }));
    secretString = resp.SecretString;
  } catch (err) {
    // Parser/transport detail KEPT, unlike the `JSON.parse` catch below.
    // Reviewed under issue #554's criterion and excluded on the same ground
    // as the `cloudfront-kvs.ts` echoes: this throw fires BEFORE any value
    // exists -- `secretString` is still undefined -- so what it relays is an
    // AWS SDK error about the ARN (AccessDenied, ResourceNotFound,
    // throttling), which is exactly what makes a credential or IAM problem
    // diagnosable. It is the one throw in this function the sibling-branch
    // test block deliberately does not cover, and this is why.
    //
    // Issue #579 narrowed WHICH SDK errors that covers, without reversing the
    // decision. LEVEL: this text reaches `ecs-service-emulator.ts`'s
    // `logger.error` -- default level, and mirrored into the studio log ring.
    // RECONSTRUCTION: `client.send(...)` resolves the credential chain before
    // the request, so a `CredentialsProviderError` carrying a
    // `credential_process` command line lands in the SAME `catch` as the
    // modeled Secrets Manager exception this comment is about. The service
    // exception's message still prints in full -- that is the half #554 kept;
    // the chain failure is the half it never considered.
    throw new EcsSecretsResolutionError(
      `Failed to resolve Secrets Manager secret for container '${entry.containerName}' / env '${entry.name}' (${shape.baseArn}): ${describeAwsFailureForWarn(
        err,
        'SecretsManager GetSecretValue'
      )}`
    );
  }
  if (secretString === undefined) {
    throw new EcsSecretsResolutionError(
      `Secrets Manager returned no SecretString for container '${entry.containerName}' / env '${entry.name}' (${shape.baseArn}). ` +
        'Binary secrets are not supported.'
    );
  }
  if (shape.jsonKey === undefined) return secretString;

  let parsed: unknown;
  try {
    parsed = JSON.parse(secretString);
  } catch (err) {
    // NEVER interpolate the parser's own message here. V8 embeds a prefix of
    // the PARSED INPUT in `SyntaxError.message`
    // (`Unexpected token 's', "supersecre"... is not valid JSON`), and it
    // TRUNCATES to that ~10-character prefix only for a long input: measured
    // on Node 24.15, an input of 20 characters or fewer is quoted in FULL,
    // and `...` first appears at 21. So a short secret leaks whole. The
    // parsed input IS the secret plaintext this resolver just fetched from
    // Secrets Manager, so echoing it puts the secret on stderr and into any
    // surrounding log capture (issue #554; reported against the cdkd host as
    // go-to-k/cdkd#2189 and fixed there by go-to-k/cdkd#2214, whose message
    // this one matches). Reaching it needs no hostile input -- a plain-string
    // secret plus a `:json-key::` ValueFrom is an ordinary user mistake.
    //
    // `err.name` is input-independent and safe as a discriminator; the
    // container, env var and requested json-key already make the message
    // actionable without showing any of the value. Deliberately NOT reported:
    // the input's LENGTH, which the sibling `$util.parseJson` message in
    // `vtl-engine.ts` does give. There the argument is an anonymous HTTP body
    // whose size is the only actionable property; here the user already knows
    // WHICH secret this is and can read it at the source, so a character
    // count would be disclosure buying nothing. Do not "harmonize" them.
    //
    // `'unknown'` rather than `'SyntaxError'` for the non-Error arm: it is
    // unreachable today (this `JSON.parse` takes no reviver, so V8 throws
    // only a SyntaxError), and reporting a class the throw was not would
    // become a lie the moment it turns reachable.
    const kind = err instanceof Error ? err.name : 'unknown';
    throw new EcsSecretsResolutionError(
      `Container '${entry.containerName}' secret '${entry.name}' specified json-key '${shape.jsonKey}' but the secret value is not valid JSON (${kind}). ` +
        'The parser detail is withheld because it would echo the secret plaintext.'
    );
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new EcsSecretsResolutionError(
      `Container '${entry.containerName}' secret '${entry.name}' specified json-key '${shape.jsonKey}' but the secret root is not a JSON object.`
    );
  }
  const value = (parsed as Record<string, unknown>)[shape.jsonKey];
  if (value === undefined) {
    throw new EcsSecretsResolutionError(
      `Container '${entry.containerName}' secret '${entry.name}' specified json-key '${shape.jsonKey}' but no such key exists in the secret JSON.`
    );
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

async function resolveSsm(entry: SecretEntry, shape: SsmShape, client: SSMClient): Promise<string> {
  try {
    const resp = await client.send(
      new GetParameterCommand({ Name: shape.name, WithDecryption: true })
    );
    const value = resp.Parameter?.Value;
    if (value === undefined) {
      throw new EcsSecretsResolutionError(
        `SSM parameter '${shape.name}' returned no Value for container '${entry.containerName}' / env '${entry.name}'.`
      );
    }
    return value;
  } catch (err) {
    if (err instanceof EcsSecretsResolutionError) throw err;
    // Issue #579 -- the SSM twin of the Secrets Manager site above, decided on
    // the same two axes. cdk-local's own "returned no Value" throw is re-raised
    // on the line above, so withholding never eats it.
    throw new EcsSecretsResolutionError(
      `Failed to resolve SSM parameter for container '${entry.containerName}' / env '${entry.name}' (${shape.name}): ${describeAwsFailureForWarn(
        err,
        'SSM GetParameter (ECS secret)'
      )}`
    );
  }
}
